import React, { useEffect, useMemo, useRef, useState } from "react";
import {Stack, ToggleButton, ToggleButtonGroup, Typography} from "@mui/material";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import OrderPickingTrend from "./OrderPickingTrend";
import DriverOrdersBreakdown from "./DriverOrdersBreakdown";
import OrderCompletionTrend from "./OrderCompletionTrend";
import SubscriptionOrderUsage from "./SubscriptionOrderUsage";
import WorkdayOverflow from "./WorkdayOverflow";
import ProblemBreakdown from "./ProblemBreakdown";
import PreparationFunnel from "./PreparationFunnel";
import ActiveRoutes from "./ActiveRoutes";
import DeliveryProgress from "./DeliveryProgress";
import RouteCompliance from "./RouteCompliance";
import {useSearchParams} from "react-router-dom";

// En que vista vive el panel que abre cada tarjeta de la cabecera de Viajes. Las cinco
// viven en KPIs: "Entregadas" era la excepcion -abria un panel de Operaciones que ya
// existia y hablaba de otra cosa- hasta que tuvo el suyo.
const VISTA_DEL_PANEL = {
    entregadas: "kpis",
    por_preparar: "kpis",
    rutas_iniciadas: "kpis",
    con_problemas: "kpis",
    fuera_de_horario: "kpis"
};

const BusinessDashboard = () => {
    const {authUser} = useJumboAuth();
    const [searchParams] = useSearchParams();
    const panelPedido = searchParams.get("panel");

    // Si se llega desde una tarjeta, se abre directo la vista donde vive su panel: pedirle
    // al usuario que cambie de pestaña despues de hacer clic seria pedirle que adivine
    // donde quedo lo que acaba de pedir.
    const [dashboardView, setDashboardView] = useState(
        () => VISTA_DEL_PANEL[panelPedido] || "operations"
    );
    const yaDesplazado = useRef(false);

    const canAccessPlanDashboard = useMemo(() => {
        const profileNames = Array.isArray(authUser?.profiles)
            ? authUser.profiles.map((profile) => profile?.name)
            : [];

        return (
            profileNames.includes("superadmin") ||
            profileNames.includes("admin") ||
            profileNames.includes("customer_admin")
        );
    }, [authUser?.profiles]);

    // Solo "Plan" esta restringida. Operaciones y KPIs las ve cualquiera, asi que el
    // selector ya no depende del perfil: antes no aparecia para nadie que no fuera admin,
    // y con tres vistas eso dejaria a la mayoria sin forma de llegar a los KPIs.
    const activeView = dashboardView === "plan" && !canAccessPlanDashboard
        ? "operations"
        : dashboardView;

    // Desplazar hasta el panel pedido no es "buscarlo y saltar": los paneles cargan sus
    // consultas en paralelo y cada uno crece cuando llegan sus datos, asi que el que esta
    // arriba del destino lo empuja despues de haber saltado. Saltando una sola vez se
    // terminaba en cualquier lado -al probarlo, en el ultimo panel de la vista-.
    //
    // Se reintenta hasta que la posicion del destino se repite dos veces seguidas, que es
    // cuando lo de arriba ya termino de crecer.
    useEffect(() => {
        if (!panelPedido) return undefined;

        // Si se llega a otro KPI sin desmontar el dashboard, hay que volver a
        // calcular la posicion: el ref queda marcado despues del primer salto.
        yaDesplazado.current = false;

        let intentos = 0;
        let ultimaFirma = null;
        let vecesQuieto = 0;
        let temporizador = null;

        const acomodar = () => {
            const destino = document.getElementById(`panel-${panelPedido}`);

            if (destino) {
                const rect = destino.getBoundingClientRect();
                const posicion = Math.round(rect.top + window.scrollY);
                const alto = Math.round(rect.height);
                const espacioVertical = window.innerHeight - alto;
                // Centrar las cajas que caben completas. Si una caja es mas alta
                // que la ventana, se deja arriba con margen para que se pueda
                // recorrer completa hacia abajo.
                const margenSuperior = Math.max(16, espacioVertical / 2);
                const destinoScroll = Math.max(0, posicion - margenSuperior);
                const firma = `${posicion}:${alto}:${Math.round(window.innerHeight)}`;

                if (firma === ultimaFirma) {
                    vecesQuieto += 1;
                } else {
                    vecesQuieto = 0;
                    ultimaFirma = firma;
                }

                window.scrollTo({top: destinoScroll, behavior: "smooth"});

                if (vecesQuieto >= 2) {
                    yaDesplazado.current = true;
                    return;
                }
            }

            intentos += 1;
            if (intentos < 40) temporizador = setTimeout(acomodar, 250);
        };
        acomodar();

        return () => {
            if (temporizador) clearTimeout(temporizador);
        };
    }, [panelPedido, activeView]);

    return (
        <Stack spacing={3}>
            <Stack
                direction={{ xs: "column", md: "row" }}
                justifyContent="space-between"
                alignItems={{ xs: "flex-start", md: "center" }}
                spacing={1.5}
            >
                <Stack spacing={0.5}>
                    <Typography variant="h2">Dashboard</Typography>
                    <Typography variant="body1" color="text.secondary">
                        Estadísticas operativas del negocio.
                    </Typography>
                </Stack>

                <ToggleButtonGroup
                    value={activeView}
                    exclusive
                    size="small"
                    onChange={(_event, value) => {
                        if (!value) return;
                        setDashboardView(value);
                    }}
                >
                    <ToggleButton value="operations">Operaciones</ToggleButton>
                    <ToggleButton value="kpis">KPIs</ToggleButton>
                    {canAccessPlanDashboard && <ToggleButton value="plan">Plan</ToggleButton>}
                </ToggleButtonGroup>
            </Stack>

            {activeView === "plan" && <SubscriptionOrderUsage/>}

            {activeView === "operations" && (
                <>
                    <OrderPickingTrend/>
                    <DriverOrdersBreakdown/>
                    <OrderCompletionTrend/>
                    <div id="panel-km_planificado"><RouteCompliance/></div>
                </>
            )}

            {activeView === "kpis" && (
                <>
                    {/* En el mismo orden que las tarjetas de la cabecera de Viajes: aca ya
                        no compiten con los paneles que la gente tenia memorizados, asi que
                        pueden seguir la secuencia del dia: preparar, salir, lo que se
                        rompe, como cerro la jornada. */}
                    <div id="panel-por_preparar"><PreparationFunnel/></div>
                    <div id="panel-rutas_iniciadas"><ActiveRoutes/></div>
                    <div id="panel-con_problemas"><ProblemBreakdown/></div>
                    <div id="panel-entregadas"><DeliveryProgress/></div>
                    <div id="panel-fuera_de_horario"><WorkdayOverflow/></div>
                </>
            )}
        </Stack>
    );
};

export default BusinessDashboard;
